import React, { forwardRef } from 'react';
import { SPRITES, RARITIES, VARIANTS, VARIANT_ORDER, MAX_LEVEL, TOTAL_SPRITES, TOTAL_LEVELS } from '../mock';
import { Crown, Flame } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';

// ─── Poster theme presets ─────────────────────────────────────────────────
// Each theme controls the poster's palette without touching layout. The
// hardcoded fallback (LLAMA LOOT) is what the poster originally shipped as.
export const POSTER_THEMES = {
  llama: {
    id: 'llama',
    name: 'Llama Loot',
    swatches: ['#ff2bd6', '#ffd900', '#00b8ff'],
    bg:
      'radial-gradient(circle at 0% 0%, #ff2bd6 0%, transparent 35%),' +
      'radial-gradient(circle at 100% 0%, #00b8ff 0%, transparent 35%),' +
      'radial-gradient(circle at 50% 40%, #ffd900 0%, transparent 45%),' +
      'radial-gradient(circle at 0% 100%, #2ee6c1 0%, transparent 35%),' +
      'radial-gradient(circle at 100% 100%, #b14bff 0%, transparent 35%),' +
      'linear-gradient(180deg, #ffe9fb 0%, #fff7d6 50%, #e9f7ff 100%)',
    bgSolid: '#fff7d6',
    frame: '#3a1064',
    frameShadow: '#1a0440',
    text: '#3a1064',
    badgeBg: '#ff2bd6',
    badgeBorder: '#ffd900',
    title1: 'chrome',
    title2: 'gold',
    plateGrad: 'linear-gradient(180deg, #ffffff 0%, #d9d9d9 45%, #8a8a8a 55%, #4a4a4a 100%)',
    plateFill: 'linear-gradient(180deg, #ffffff 0%, #ffffff 42%, #7d7d7d 55%, #2b2b2b 100%)',
    plateStroke: '#1a0440',
    stat1: { bg: '#ffd900', fg: '#3a1064' },
    stat2: { bg: '#ff2bd6', fg: '#fff' },
    stat3: { bg: '#00b8ff', fg: '#fff' },
    card: '#fff',
    footerBg: '#3a1064',
    footerBorder: '#ffd900',
    footerAccent: '#ffd900',
    footerLabel: '#fff',
    missingBg: '#f5eeff',
    missingBorder: '#cbb9e8',
  },
  ice: {
    id: 'ice',
    name: 'Ice Storm',
    swatches: ['#00e5ff', '#0057ff', '#b6f1ff'],
    bg:
      'radial-gradient(circle at 0% 0%, #00e5ff 0%, transparent 35%),' +
      'radial-gradient(circle at 100% 0%, #b6f1ff 0%, transparent 40%),' +
      'radial-gradient(circle at 50% 100%, #6ea8ff 0%, transparent 45%),' +
      'linear-gradient(180deg, #ffffff 0%, #e6f6ff 55%, #cfe4ff 100%)',
    bgSolid: '#e6f6ff',
    frame: '#0a2557',
    frameShadow: '#03153a',
    text: '#0a2557',
    badgeBg: '#00b8ff',
    badgeBorder: '#ffffff',
    title1: 'chrome',
    title2: 'chrome',
    plateGrad: 'linear-gradient(180deg, #ffffff 0%, #cfe4ff 45%, #6ea8ff 65%, #0a2557 100%)',
    plateFill: 'linear-gradient(180deg, #ffffff 0%, #eaf6ff 45%, #62b4ff 65%, #0a1f4c 100%)',
    plateStroke: '#03153a',
    stat1: { bg: '#00b8ff', fg: '#ffffff' },
    stat2: { bg: '#ffffff', fg: '#0a2557' },
    stat3: { bg: '#6ea8ff', fg: '#ffffff' },
    card: '#ffffff',
    footerBg: '#0a2557',
    footerBorder: '#00e5ff',
    footerAccent: '#00e5ff',
    footerLabel: '#e6f6ff',
    missingBg: '#eef4ff',
    missingBorder: '#b8c8e3',
  },
  midas: {
    id: 'midas',
    name: 'Midas Gold',
    swatches: ['#ffd900', '#b78500', '#1a1a1a'],
    bg:
      'radial-gradient(circle at 0% 0%, #ffd900 0%, transparent 40%),' +
      'radial-gradient(circle at 100% 100%, #b78500 0%, transparent 40%),' +
      'radial-gradient(circle at 50% 40%, #fff6b3 0%, transparent 40%),' +
      'linear-gradient(180deg, #1a1305 0%, #2a1e08 60%, #100a02 100%)',
    bgSolid: '#1a1305',
    frame: '#ffd900',
    frameShadow: '#7a5d00',
    text: '#ffd900',
    badgeBg: '#ffd900',
    badgeBorder: '#1a1305',
    title1: 'gold',
    title2: 'gold',
    plateGrad: 'linear-gradient(180deg, #fff6b3 0%, #ffd900 45%, #a17500 100%)',
    plateFill: 'linear-gradient(180deg, #ffffff 0%, #fff7c2 24%, #ffd900 55%, #ff8c1a 92%, #b74100 100%)',
    plateStroke: '#3a2a00',
    stat1: { bg: '#ffd900', fg: '#1a1305' },
    stat2: { bg: '#1a1305', fg: '#ffd900' },
    stat3: { bg: '#b78500', fg: '#ffffff' },
    card: '#1e1608',
    footerBg: '#000000',
    footerBorder: '#ffd900',
    footerAccent: '#ffd900',
    footerLabel: '#fff6b3',
    missingBg: '#231a08',
    missingBorder: '#4a3500',
  },
  neon: {
    id: 'neon',
    name: 'Neon Nights',
    swatches: ['#ff2bd6', '#7b2bff', '#00e5ff'],
    bg:
      'radial-gradient(circle at 0% 0%, #ff2bd6 0%, transparent 35%),' +
      'radial-gradient(circle at 100% 0%, #00e5ff 0%, transparent 35%),' +
      'radial-gradient(circle at 50% 100%, #7b2bff 0%, transparent 45%),' +
      'linear-gradient(180deg, #0a0018 0%, #14002e 55%, #05000f 100%)',
    bgSolid: '#0a0018',
    frame: '#ff2bd6',
    frameShadow: '#5a0044',
    text: '#ffffff',
    badgeBg: '#ff2bd6',
    badgeBorder: '#00e5ff',
    title1: 'chrome',
    title2: 'gold',
    plateGrad: 'linear-gradient(180deg, #ffffff 0%, #ff2bd6 45%, #7b2bff 65%, #05000f 100%)',
    plateFill: 'linear-gradient(180deg, #ffffff 0%, #f5c8ff 25%, #ff2bd6 55%, #7b2bff 90%, #05000f 100%)',
    plateStroke: '#05000f',
    stat1: { bg: '#ff2bd6', fg: '#ffffff' },
    stat2: { bg: '#00e5ff', fg: '#0a0018' },
    stat3: { bg: '#7b2bff', fg: '#ffffff' },
    card: '#12002a',
    footerBg: '#000000',
    footerBorder: '#00e5ff',
    footerAccent: '#00e5ff',
    footerLabel: '#ff2bd6',
    missingBg: '#1a0a30',
    missingBorder: '#3a1a60',
  },
  verdant: {
    id: 'verdant',
    name: 'Verdant Vault',
    swatches: ['#2ee6c1', '#0ea36e', '#c8ffea'],
    bg:
      'radial-gradient(circle at 0% 0%, #2ee6c1 0%, transparent 40%),' +
      'radial-gradient(circle at 100% 100%, #0ea36e 0%, transparent 40%),' +
      'radial-gradient(circle at 50% 30%, #c8ffea 0%, transparent 40%),' +
      'linear-gradient(180deg, #e6fff5 0%, #ffffff 55%, #d3f9e5 100%)',
    bgSolid: '#e6fff5',
    frame: '#064733',
    frameShadow: '#022417',
    text: '#064733',
    badgeBg: '#0ea36e',
    badgeBorder: '#c8ffea',
    title1: 'chrome',
    title2: 'green',
    plateGrad: 'linear-gradient(180deg, #ffffff 0%, #c8ffea 45%, #2ee6c1 65%, #064733 100%)',
    plateFill: 'linear-gradient(180deg, #ffffff 0%, #d3f9e5 40%, #0ea36e 70%, #022417 100%)',
    plateStroke: '#022417',
    stat1: { bg: '#0ea36e', fg: '#ffffff' },
    stat2: { bg: '#2ee6c1', fg: '#064733' },
    stat3: { bg: '#ffffff', fg: '#064733' },
    card: '#ffffff',
    footerBg: '#064733',
    footerBorder: '#2ee6c1',
    footerAccent: '#2ee6c1',
    footerLabel: '#c8ffea',
    missingBg: '#eafaf1',
    missingBorder: '#b7d9c9',
  },
};

// Font stacks used across the poster. Pinned to Google Fonts (loaded in
// index.html) so html-to-image captures them consistently across devices
// instead of falling back to Impact/system-ui.
const FONT_DISPLAY = "'Burbank Big Condensed Black', 'Fortnite', 'Titan One', 'Bowlby One SC', 'Passion One', 'Impact', system-ui, sans-serif"; // wordmarks
const FONT_HEADING = "'Burbank Big Condensed Black', 'Fjalla One', 'Titan One', 'Impact', system-ui, sans-serif";                    // labels + banners
const FONT_BODY = "'Rubik', 'Inter', system-ui, sans-serif";                                          // stat values + body

export const POSTER_THEME_LIST = Object.values(POSTER_THEMES);

// Color per sprite family for the banner ribbon
const FAMILY_COLORS = {
  Water: '#1da7ff',
  Fire: '#ff5b3a',
  Earth: '#3aa84e',
  Duck: '#ffb700',
  Ghost: '#a86bff',
  Dream: '#7a4dff',
  Demon: '#c61f3b',
  Punk: '#ff2bd6',
  King: '#ff8a1c',
  'Burnt Peanut': '#c98a3a',
  'Zero Point': '#00b8ff',
  Fishy: '#1ab39a',
  Striker: '#0f6cff',
  Aura: '#5a64ff',
  Boss: '#5a2ad6',
  'Grim Reaper': '#5e4a8a',
};

function buildFamilies(collection, dataUrlMap, season = 'all', sprites = SPRITES) {
  const map = new Map();
  for (const s of sprites) {
    if (season !== 'all' && (s.season || 'C7S3') !== season) continue;
    if (!map.has(s.family)) map.set(s.family, []);
    map.get(s.family).push(s);
  }
  // sort each family by variant order
  for (const arr of map.values()) {
    arr.sort((a, b) => VARIANT_ORDER.indexOf(a.variant) - VARIANT_ORDER.indexOf(b.variant));
  }
  return Array.from(map.entries()).map(([family, sprites]) => ({
    family,
    sprites: sprites.map((s) => ({
      ...s,
      _entry: collection[s.id] || { owned: false, level: 0, mastered: false },
      _img: dataUrlMap?.[s.img] || s.img,
    })),
  }));
}

const SharePoster = forwardRef(function SharePoster(
  { collection, sprites = SPRITES, dataUrlMap, siteUrl = 'spritevault.app', themeId = 'llama', filterStatus = 'all', season = 'all', profileUrl = null, username = null, headerImageUrl = null },
  ref
) {
  const theme = POSTER_THEMES[themeId] || POSTER_THEMES.llama;
  // Pool respects the current season filter so counts + families match
  // what the tracker is showing when the poster was requested.
  const posterPool = season === 'all' ? sprites : sprites.filter((s) => (s.season || 'C7S3') === season);
  const ownedCount = posterPool.filter((s) => collection[s.id]?.owned).length;
  const masteredCount = posterPool.filter((s) => collection[s.id]?.mastered).length;
  const levelsSum = posterPool.reduce((acc, s) => acc + (collection[s.id]?.level || 0), 0);

  const rawFamilies = buildFamilies(collection, dataUrlMap, season, sprites);

  // Apply the active tracker filter to the poster. When the user has the
  // "Missing" filter on, the exported PNG should only show sprites they
  // still need to catch — a "want list" they can share to trigger trades.
  // Owned / Mastered work the same way. Empty families are dropped so we
  // don't render blank cards.
  const families = rawFamilies
    .map((f) => {
      let kept = f.sprites;
      if (filterStatus === 'missing')  kept = kept.filter((s) => !s._entry.owned);
      if (filterStatus === 'owned')    kept = kept.filter((s) =>  s._entry.owned);
      if (filterStatus === 'mastered') kept = kept.filter((s) =>  s._entry.mastered);
      return { ...f, sprites: kept };
    })
    .filter((f) => f.sprites.length > 0);

  // Separate single-variant "compact" families (like Burnt Peanut) — they
  // pair together on one row so they don't waste a full slot.
  const fullFamilies = families.filter((f) => f.sprites.length > 1);
  const compactFamilies = families.filter((f) => f.sprites.length === 1);

  // Label the poster subtitle so recipients understand what the export shows.
  const filterLabel =
    filterStatus === 'missing'  ? { text: `MISSING SPRITES · ${posterPool.length - ownedCount} to catch`, color: '#ff2bd6' } :
    filterStatus === 'owned'    ? { text: `OWNED · ${ownedCount}/${posterPool.length} collected`,       color: '#2ee6c1' } :
    filterStatus === 'mastered' ? { text: `MASTERED · ${masteredCount} sprites`,                        color: '#ffd900' } :
    null;

  return (
    <div
      ref={ref}
      style={{
        width: 1400,
        // Force a dark space background for the loot-card aesthetic.
        // Individual family cards carry the color theming via their borders.
        background:
          'radial-gradient(ellipse at 20% 10%, rgba(255,43,214,0.15) 0%, transparent 45%),' +
          'radial-gradient(ellipse at 80% 20%, rgba(0,184,255,0.15) 0%, transparent 45%),' +
          'radial-gradient(ellipse at 50% 100%, rgba(123,43,255,0.20) 0%, transparent 55%),' +
          'radial-gradient(circle at 15% 60%, #ffffff08 0px, transparent 1px),' +
          'radial-gradient(circle at 85% 40%, #ffffff10 0px, transparent 1px),' +
          'linear-gradient(180deg, #0a0018 0%, #050010 45%, #02000a 100%)',
        fontFamily: FONT_BODY,
        color: '#ffffff',
        padding: 40,
        boxSizing: 'border-box',
      }}
    >
      {/* Title — AI-generated FORTNITE SPRITES TRACKER logo PNG
          (rendered once via Gemini Nano Banana at build time and stored
          in /public/branding/poster-header.png). Using an image guarantees
          the header rasterises to the exported PNG with pixel-perfect
          fidelity — html-to-image can't mangle a bitmap the way it does
          web fonts / SVG text on mobile browsers. */}
      <div style={{ textAlign: 'center', marginBottom: 22, marginTop: 0 }}>
        <img
          src={headerImageUrl || '/branding/poster-header.png'}
          alt="Fortnite Sprites Tracker"
          style={{
            display: 'block',
            width: '92%',
            maxWidth: 1280,
            height: 'auto',
            margin: '0 auto',
            borderRadius: 20,
            // Soft edge blend into the dark poster background so the image's
            // solid dark-purple canvas doesn't show a visible rectangle seam.
            WebkitMaskImage:
              'radial-gradient(ellipse 90% 80% at 50% 50%, #000 60%, transparent 100%)',
            maskImage:
              'radial-gradient(ellipse 90% 80% at 50% 50%, #000 60%, transparent 100%)',
            filter: 'drop-shadow(0 8px 24px rgba(0,0,0,0.55))',
          }}
        />
        {filterLabel && (
          <div
            style={{
              display: 'inline-block',
              marginTop: 6,
              padding: '10px 26px',
              background: '#0a0018',
              border: `4px solid ${filterLabel.color}`,
              borderRadius: 999,
              fontFamily: FONT_HEADING,
              letterSpacing: 5,
              fontSize: 26,
              color: filterLabel.color,
              boxShadow: `0 0 22px ${filterLabel.color}55, 0 6px 0 rgba(0,0,0,0.6)`,
              textShadow: '0 2px 0 rgba(0,0,0,0.6)',
            }}
          >
            {filterLabel.text}
          </div>
        )}
      </div>

      {/* Family grid — 3 dense columns like a game roster board */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 14,
        }}
      >
        {fullFamilies.map((fam) => (
          <FamilyCard key={fam.family} family={fam.family} sprites={fam.sprites} theme={theme} />
        ))}
      </div>

      {/* Compact row for single-variant families (e.g. Burnt Peanut, John Wick).
          Shrunk to ~1/6 of poster width and centered so the single sprite
          inside doesn't dwarf the sprites in the full 6-column family cards
          above. A small legend card sits next to them explaining the mastered
          badge and level flames. */}
      {compactFamilies.length > 0 && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'stretch',
            gap: 14,
            marginTop: 14,
          }}
        >
          {compactFamilies.map((fam) => (
            <div key={fam.family} style={{ width: 200, flexShrink: 0 }}>
              <FamilyCard family={fam.family} sprites={fam.sprites} theme={theme} compact />
            </div>
          ))}
          <PosterLegend theme={theme} />
        </div>
      )}

      {/* Footer — big QR code + prominent website URL. If a signed-in user
          is present, the QR routes straight to their public trade profile;
          otherwise it points to the generic sprite-vault.com landing page. */}
      <PosterFooter
        siteUrl={siteUrl}
        profileUrl={profileUrl}
        username={username}
        theme={theme}
      />
    </div>
  );
});

// ─── Footer with QR code + big website URL ─────────────────────────────────
function PosterFooter({ siteUrl, profileUrl, username, theme }) {
  // QR always points to the "best" landing spot for this poster:
  //   - signed-in user  → their public profile (/u/username) for one-tap trade
  //   - anonymous share → generic www.sprite-vault.com
  const qrTarget = profileUrl || 'https://www.sprite-vault.com';
  const displayUrl = username
    ? `sprite-vault.com/u/${username}`
    : 'www.sprite-vault.com';

  return (
    <div
      style={{
        marginTop: 26,
        display: 'flex',
        alignItems: 'center',
        gap: 22,
        padding: '18px 22px',
        background: 'rgba(255,255,255,0.05)',
        border: `3px solid ${theme.footerAccent}`,
        borderRadius: 20,
        boxShadow: `0 0 26px ${theme.footerAccent}33, inset 0 0 22px rgba(0,0,0,0.35)`,
      }}
    >
      {/* QR panel — white rounded chip for max scan reliability */}
      <div
        style={{
          background: '#ffffff',
          padding: 14,
          borderRadius: 16,
          boxShadow: '0 6px 0 rgba(0,0,0,0.45)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 6,
          flexShrink: 0,
        }}
      >
        <QRCodeSVG
          value={qrTarget}
          size={180}
          level="H"
          bgColor="#ffffff"
          fgColor="#0a0018"
          includeMargin={false}
        />
        <div
          style={{
            fontFamily: FONT_HEADING,
            fontSize: 15,
            letterSpacing: 3,
            color: '#0a0018',
          }}
        >
          {profileUrl ? 'SCAN TO TRADE' : 'SCAN TO OPEN'}
        </div>
      </div>

      {/* Big website URL — chunky Fortnite font, huge and readable across the room */}
      <div style={{ flex: 1, textAlign: 'center' }}>
        <div
          style={{
            fontFamily: FONT_HEADING,
            fontSize: 22,
            letterSpacing: 5,
            color: theme.footerAccent,
            opacity: 0.85,
            marginBottom: 6,
          }}
        >
          {profileUrl ? `TRADE WITH @${(username || '').toUpperCase()}` : 'COLLECT · TRACK · TRADE'}
        </div>
        <div
          style={{
            fontFamily: FONT_HEADING,
            fontSize: 64,
            lineHeight: 1,
            letterSpacing: 4,
            color: '#ffffff',
            WebkitTextStroke: `4px ${theme.frameShadow || '#04102e'}`,
            textShadow: `0 6px 0 ${theme.frameShadow || '#04102e'}, 0 12px 24px rgba(0,0,0,0.6)`,
            wordBreak: 'break-word',
          }}
        >
          {displayUrl.toUpperCase()}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, bg, fg, frame = '#3a1064' }) {
  return (
    <div
      style={{
        background: bg,
        color: fg,
        padding: '14px 30px',
        borderRadius: 16,
        border: `4px solid ${frame}`,
        boxShadow: `0 4px 0 ${frame}`,
        textAlign: 'center',
      }}
    >
      <div style={{ fontFamily: FONT_HEADING, fontSize: 15, letterSpacing: 4, opacity: 0.95 }}>
        {label.toUpperCase()}
      </div>
      <div style={{ fontFamily: FONT_BODY, fontWeight: 900, fontSize: 34, lineHeight: 1, marginTop: 6, letterSpacing: 0.5 }}>
        {value}
      </div>
    </div>
  );
}

function LegendItem({ children }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        background: '#fff',
        border: '4px solid #3a1064',
        borderRadius: 16,
        boxShadow: '0 4px 0 #3a1064',
        padding: '10px 16px',
      }}
    >
      {children}
    </div>
  );
}

function LegendText({ title, sub }) {
  return (
    <div style={{ textAlign: 'left' }}>
      <div style={{ fontFamily: FONT_HEADING, fontSize: 19, letterSpacing: 2.5, color: '#3a1064', lineHeight: 1 }}>
        {title}
      </div>
      <div style={{ fontFamily: FONT_BODY, fontWeight: 700, fontSize: 13, color: '#3a1064', opacity: 0.75, marginTop: 5 }}>
        {sub}
      </div>
    </div>
  );
}

function FamilyCard({ family, sprites, compact = false, theme }) {
  const color = FAMILY_COLORS[family] || theme?.frame || '#ff2bd6';
  return (
    <div
      style={{
        // Dark starry-glass card body — matches the reference "loot card".
        background:
          'radial-gradient(ellipse at 30% 20%, rgba(255,255,255,0.05) 0%, transparent 60%),' +
          'linear-gradient(180deg, #0e0426 0%, #05010f 100%)',
        border: `3px solid ${color}`,
        borderRadius: 16,
        boxShadow: `0 0 26px ${color}55, inset 0 0 24px ${color}22`,
        padding: compact ? 12 : 14,
        paddingTop: compact ? 12 : 16,
        position: 'relative',
      }}
    >
      {/* Family banner — colored pill on the card lip */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          marginTop: compact ? -22 : -28,
          marginBottom: compact ? 8 : 10,
        }}
      >
        <div
          style={{
            display: 'inline-block',
            background: `linear-gradient(180deg, ${color} 0%, ${color}cc 100%)`,
            color: '#fff',
            padding: compact ? '6px 20px' : '8px 26px',
            borderRadius: 999,
            border: `3px solid #05010f`,
            fontFamily: FONT_HEADING,
            letterSpacing: 4,
            fontSize: compact ? 14 : 20,
            boxShadow: `0 0 18px ${color}, 0 3px 0 #05010f`,
            textShadow: '0 2px 0 rgba(0,0,0,0.35)',
          }}
        >
          {family.toUpperCase()}
        </div>
      </div>

      {/* Sprite row */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${sprites.length}, 1fr)`,
          gap: 6,
        }}
      >
        {sprites.map((s) => (
          <SpriteCell key={s.id} sprite={s} theme={theme} accent={color} />
        ))}
      </div>
    </div>
  );
}

function SpriteCell({ sprite, theme, accent = '#ff2bd6' }) {
  const entry = sprite._entry;
  const owned = entry.owned;
  const mastered = entry.mastered;

  return (
    <div style={{ textAlign: 'center' }}>
      {/* Image tile — dark starry background, no variant chip */}
      <div
        style={{
          position: 'relative',
          aspectRatio: '1 / 1',
          width: '100%',
          borderRadius: 10,
          background:
            'radial-gradient(circle at 40% 30%, rgba(255,255,255,0.06) 0%, transparent 60%),' +
            'linear-gradient(180deg, #0b0320 0%, #05010f 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        {mastered && (
          <div
            style={{
              position: 'absolute',
              top: 3,
              right: 3,
              width: 22,
              height: 22,
              borderRadius: '50%',
              background: '#ffd900',
              border: '2px solid #fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 4px rgba(0,0,0,0.35)',
              zIndex: 2,
            }}
          >
            <Crown size={12} color="#7a4c00" fill="#7a4c00" />
          </div>
        )}
        <img
          src={sprite._img}
          alt={sprite.name}
          style={{
            width: '92%',
            height: '92%',
            objectFit: 'contain',
            filter: owned ? 'drop-shadow(0 3px 6px rgba(0,0,0,0.55))' : 'grayscale(1) brightness(0.7)',
            opacity: owned ? 1 : 0.55,
          }}
        />
      </div>

      {/* Neon checkbox — pink glowing outline; filled when owned */}
      <div
        style={{
          marginTop: 6,
          width: '100%',
          aspectRatio: '1 / 1',
          maxHeight: 26,
          borderRadius: 4,
          border: `2px solid ${accent}`,
          background: owned ? accent : 'transparent',
          boxShadow: `0 0 8px ${accent}88`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {owned && (
          <div
            style={{
              fontFamily: FONT_HEADING,
              color: '#fff',
              fontSize: 16,
              lineHeight: 1,
              textShadow: '0 1px 0 rgba(0,0,0,0.5)',
            }}
          >
            ✓
          </div>
        )}
      </div>

      {/* Level row with flames — small, only when >0 */}
      {entry.level > 0 && (
        <div
          style={{
            marginTop: 4,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
          }}
        >
          {Array.from({ length: MAX_LEVEL }).map((_, i) => (
            <Flame
              key={i}
              size={11}
              color={i < entry.level ? '#ff6a1c' : '#3a1a4a'}
              fill={i < entry.level ? '#ff9a1c' : 'transparent'}
              strokeWidth={2}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default SharePoster;

// ─── Poster Legend (mastered badge + level flames) ─────────────────────────
// Sits next to the compact Burnt Peanut / John Wick cards so viewers can
// decode the tiny icons scattered across the poster.
function PosterLegend({ theme }) {
  return (
    <div
      style={{
        width: 260,
        flexShrink: 0,
        padding: '14px 16px',
        borderRadius: 18,
        background: 'rgba(0,0,0,0.35)',
        border: `3px solid ${theme.footerAccent || '#ffd900'}`,
        boxShadow: `0 6px 0 ${theme.frameShadow || '#04102e'}, 0 0 22px ${
          theme.footerAccent || '#ffd900'
        }33`,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
      }}
    >
      <div
        style={{
          fontFamily: FONT_HEADING,
          fontSize: 18,
          letterSpacing: 4,
          textAlign: 'center',
          color: theme.footerAccent || '#ffd900',
          textShadow: `0 2px 0 ${theme.frameShadow || '#04102e'}`,
        }}
      >
        LEGEND
      </div>

      {/* Mastered row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div
          style={{
            width: 26,
            height: 26,
            borderRadius: '50%',
            background: '#ffd900',
            border: '2px solid #fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 4px rgba(0,0,0,0.35)',
            flexShrink: 0,
          }}
        >
          <Crown size={14} color="#7a4c00" fill="#7a4c00" />
        </div>
        <div
          style={{
            fontFamily: FONT_HEADING,
            fontSize: 15,
            letterSpacing: 2,
            color: '#ffffff',
            textShadow: '0 2px 0 rgba(0,0,0,0.5)',
          }}
        >
          MASTERED
        </div>
      </div>

      {/* Level row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ display: 'flex', gap: 1, flexShrink: 0 }}>
          {Array.from({ length: MAX_LEVEL }).map((_, i) => (
            <Flame
              key={i}
              size={14}
              color="#ff6a1c"
              fill="#ff9a1c"
              strokeWidth={2}
            />
          ))}
        </div>
        <div
          style={{
            fontFamily: FONT_HEADING,
            fontSize: 15,
            letterSpacing: 2,
            color: '#ffffff',
            textShadow: '0 2px 0 rgba(0,0,0,0.5)',
          }}
        >
          LEVEL
        </div>
      </div>
    </div>
  );
}

// ─── SVG Fortnite-style headline (crisp export, chrome + gold gradient) ─────
// Renders text as SVG so we get real gradient fills + stroke outlines with
// no rasterisation blur, matching a proper game roster wordmark.
function FortniteHeadline({ lines }) {
  const width = 1200;
  const rowHeight = 200;
  const totalHeight = rowHeight * lines.length + 30;

  return (
    <svg
      width={width}
      height={totalHeight}
      viewBox={`0 0 ${width} ${totalHeight}`}
      xmlns="http://www.w3.org/2000/svg"
      style={{ display: 'block', margin: '0 auto' }}
    >
      <defs>
        {/* Chrome (top row): white → sky blue → navy */}
        <linearGradient id="sv-chrome" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#ffffff" />
          <stop offset="42%" stopColor="#dcefff" />
          <stop offset="55%" stopColor="#3d8bff" />
          <stop offset="90%" stopColor="#0d3a8a" />
          <stop offset="100%" stopColor="#04102e" />
        </linearGradient>
        {/* Gold (bottom row): cream → gold → deep orange */}
        <linearGradient id="sv-gold" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#ffffff" />
          <stop offset="30%" stopColor="#fff2a0" />
          <stop offset="55%" stopColor="#ffcc00" />
          <stop offset="88%" stopColor="#e07a00" />
          <stop offset="100%" stopColor="#7a2200" />
        </linearGradient>
        <filter id="sv-drop" x="-10%" y="-10%" width="120%" height="130%">
          <feDropShadow dx="0" dy="10" stdDeviation="0" floodColor="#000" floodOpacity="0.55" />
          <feDropShadow dx="0" dy="0" stdDeviation="14" floodColor="#ff2bd6" floodOpacity="0.35" />
        </filter>
      </defs>

      {lines.map((text, i) => (
        <g key={text} transform={`translate(${width / 2}, ${rowHeight * (i + 1) - 40})`}>
          <text
            textAnchor="middle"
            fontFamily="'Burbank Big Condensed Black', 'Fortnite', 'Titan One', 'Impact', sans-serif"
            fontSize={200}
            fontWeight={900}
            letterSpacing={4}
            stroke="#04102e"
            strokeWidth={7}
            strokeLinejoin="round"
            paintOrder="stroke"
            fill={i === 0 ? 'url(#sv-chrome)' : 'url(#sv-gold)'}
            filter="url(#sv-drop)"
          >
            {text}
          </text>
        </g>
      ))}
    </svg>
  );
}


// ─── Fortnite-style word — chrome/blue OR gold gradient with thick outline ──
function FortniteWord({ text, tint = 'chrome', size = 96 }) {
  // Colorways: 'chrome' (white → cyan-blue), 'gold' (yellow → orange),
  // 'green' (white → teal/emerald). Single-layer render for crisp export.
  let gradient;
  let strokeColor;
  if (tint === 'gold') {
    gradient = 'linear-gradient(180deg, #ffffff 0%, #fff7c2 24%, #ffd900 55%, #ff8c1a 92%, #b74100 100%)';
    strokeColor = '#3a1064';
  } else if (tint === 'green') {
    gradient = 'linear-gradient(180deg, #ffffff 0%, #d3f9e5 25%, #2ee6c1 55%, #0ea36e 92%, #022417 100%)';
    strokeColor = '#022417';
  } else {
    gradient = 'linear-gradient(180deg, #ffffff 0%, #eaf6ff 28%, #62b4ff 55%, #1e5fbf 90%, #0a1f4c 100%)';
    strokeColor = '#0b2755';
  }
  return (
    <span
      style={{
        display: 'inline-block',
        fontFamily: FONT_DISPLAY,
        fontSize: size,
        fontWeight: 400,
        letterSpacing: 4,
        lineHeight: 1,
        background: gradient,
        WebkitBackgroundClip: 'text',
        backgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        color: 'transparent',
        WebkitTextStroke: `3px ${strokeColor}`,
        filter:
          'drop-shadow(0 5px 0 rgba(0,0,0,0.4)) drop-shadow(0 12px 20px rgba(0,0,0,0.5))',
      }}
    >
      {text}
    </span>
  );
}

